import { ChangeDetectionStrategy, Component, forwardRef, Input } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { MaskitoOptions } from '@maskito/core';
import { FormValidationUtils } from '../../../../../../utils/form-validation-utils';
import { DateConversionUtils } from '../../../../../../utils/date-conversion-utils';

@Component({
  selector: 'app-review-masked-date-input',
  templateUrl: './review-masked-date-input.component.html',
  styleUrls: ['./review-masked-date-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ReviewMaskedDateInputComponent),
      multi: true
    }
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReviewMaskedDateInputComponent {
  @Input() placeholder: string = '';
  @Input() controlId: string | null = null;
  @Input() error: boolean = false;

  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};

  public value = '';
  public disabled = false;

  public dateMask: MaskitoOptions = {
    mask: [/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/]
  };

  public onInput(value: string): void {
    this.value = value;

    if (!value) {
      this.onChange(null);
      return;
    }

    const [day, month, year] = value.split('/');
    if (day && month && year) {
      const utcDate = new Date(Date.UTC(+year, +month - 1, +day));
      this.onChange(DateConversionUtils.dateToNativeInput(utcDate)); // emits date-only, e.g. "2024-01-01"
    } else {
      this.onChange(FormValidationUtils.INVALID_DATE_MARKER); // incomplete input
    }
  }

  writeValue(value: string | null): void {
    // Extracted directly from the date-only string via regex, without ever constructing a `Date` -
    // no dependency on native Date parsing's date-only-vs-datetime distinction, which is easy to
    // get backwards.
    const parts = value ? DateConversionUtils.parseDateOnlyParts(value) : null;
    if (parts) {
      const day = String(parts.day).padStart(2, '0');
      const month = String(parts.month).padStart(2, '0');
      this.value = `${day}/${month}/${parts.year}`;
    } else {
      this.value = '';
    }
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
